import { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha, useTheme } from "@mui/material/styles";
import {
  REVIEW_NOTE_MAX,
  REVIEW_RATINGS,
  REVIEW_RATING_LABELS,
  type Conversation,
  type ConversationReview,
  type ReviewEntry,
  type ReviewRating,
} from "@common/domain/conversation-results";
import { OUTCOME_LABEL, catCode, entryOf, reviewerName, runStats, severityOf } from "./resultsLogic";
import { OutcomePip, SeverityChip } from "./shared";

export type TurnKey = number | "overall";

interface Props {
  conversation: Conversation;
  reviews: ConversationReview[]; // reviews of this conversation the viewer may see (blind: usually only their own)
  /** How many reviewers have reviewed this conversation, counting everyone. */
  reviewCount: number;
  viewerId: string;
  onSave: (turn: TurnKey, patch: ReviewEntry) => void;
  onClear: () => void;
  onClose: () => void;
  /** True for a file opened locally: reviews are kept in this browser instead of on the server. */
  savedLocally: boolean;
  /** Super admins receive every review (reviewScope "all") and can inspect the team's reviews here. */
  showTeam: boolean;
}

/** Every other reviewer's ratings and notes, one block per reviewer. Only rendered for super admins. */
function TeamReviews({ reviews }: { reviews: ConversationReview[] }) {
  const line = (label: string, e: ReviewEntry) =>
    e.rating || e.note ? (
      <Typography key={label} variant="body2">
        <Box component="span" sx={{ color: "text.secondary" }}>{label}: </Box>
        {e.rating ? REVIEW_RATING_LABELS[e.rating] : "no rating"}{e.note ? ` · ${e.note}` : ""}
      </Typography>
    ) : null;
  return (
    <Accordion disableGutters variant="outlined" sx={{ mx: 2, mt: 1.5, borderRadius: 2, "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 500 }}>Team reviews ({reviews.length})</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1.5, alignSelf: "center" }}>Visible to super admins only</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {reviews.map((r) => (
            <Box key={r.userId}>
              <Typography sx={{ fontWeight: 500 }}>{reviewerName(r)}</Typography>
              {line("All replies", r.overall || {})}
              {Object.keys(r.turns || {}).sort((a, b) => Number(a) - Number(b)).map((t) => line(`T${t} reply`, r.turns[t] || {}))}
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function Field({ k, v }: { k: string; v: unknown }) {
  if (v === "" || v == null) return null;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "120px minmax(0,1fr)", gap: 1.25, py: 0.9, borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}>
      <Typography variant="caption" color="text.secondary">{k}</Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>{String(v)}</Typography>
    </Box>
  );
}

function Message({ who, text, reply }: { who: string; text: string; reply?: boolean }) {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{who}</Typography>
      <Box sx={{
        whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, px: 1.75, py: 1.25, borderRadius: 2, borderTopLeftRadius: 4, maxWidth: "92ch",
        bgcolor: reply ? alpha(theme.palette.background.paper, 0.85) : alpha(theme.palette.text.primary, 0.05),
        border: reply ? 1 : 0, borderColor: "divider", ml: reply ? { xs: 0, sm: 4 } : 0,
      }}>
        {text || "(no reply)"}
      </Box>
    </Box>
  );
}

/** Rating buttons + note for one reply (or all replies). Reviews are blind, so only the viewer's own are shown. */
function ReviewControls({ turn, mine, onSave }: {
  turn: TurnKey; mine: ReviewEntry; onSave: Props["onSave"];
}) {
  const theme = useTheme();
  const [note, setNote] = useState(mine.note || "");
  useEffect(() => setNote(mine.note || ""), [mine.note]);
  const label = turn === "overall" ? "All replies" : "Reply";
  const ratingColor: Record<ReviewRating, string> = {
    good: theme.palette.secondary.main, acceptable: theme.palette.primary.main, poor: theme.palette.error.main,
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", ml: turn === "overall" ? 0 : { xs: 0, sm: 4 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mine.rating ?? null}
        onChange={(_, v: ReviewRating | null) => onSave(turn, { rating: v })}
        aria-label={turn === "overall" ? "Rate all persona replies" : `Rate turn ${turn} reply`}
      >
        {REVIEW_RATINGS.map((r) => (
          <ToggleButton key={r} value={r} sx={{ py: 0.25, px: 1.5, textTransform: "none", "&.Mui-selected": { color: ratingColor[r], bgcolor: alpha(ratingColor[r], 0.12) } }}>
            {REVIEW_RATING_LABELS[r]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        size="small"
        multiline
        maxRows={6}
        placeholder="Note (optional)"
        inputProps={{ "aria-label": `${label} note`, maxLength: REVIEW_NOTE_MAX }}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => { if (note.trim() !== (mine.note || "")) onSave(turn, { note: note.trim() }); }}
        sx={{ flex: "1 1 220px" }}
      />
    </Box>
  );
}

export default function ConversationDetail({ conversation: c, reviews, reviewCount, viewerId, onSave, onClear, onClose, savedLocally, showTeam }: Props) {
  const theme = useTheme();
  const mine = reviews.find((r) => r.userId === viewerId);
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);

  const controls = (turn: TurnKey) => (
    <ReviewControls
      turn={turn}
      mine={mine ? entryOf(mine, turn) : {}}
      onSave={onSave}
    />
  );

  const title = c.kind === "scenario" ? c.label : `${c.attack} · ${c.category}`;
  const st = c.kind === "run" ? runStats([c]) : null;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "baseline", justifyContent: "space-between", px: 2, pt: 1.75, pb: 0.5 }}>
        <Typography variant="h6" component="h2">
          {title}{" "}
          {c.kind === "scenario" && <Typography component="span" variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>{c.scenarioId}</Typography>}
        </Typography>
        <Button variant="outlined" size="small" onClick={onClose}>Close</Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0,340px) minmax(0,1fr)" } }}>
        <Box sx={{ px: 2, pt: 1, pb: 2, borderRight: { md: 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: "divider" }}>
          {c.kind === "scenario" ? (
            <>
              <Box sx={{ py: 0.9 }}><SeverityChip severity={severityOf(c)} /></Box>
              <Field k="label" v={c.label} />
              <Field k="bri_category" v={`${catCode(c.cat)} · ${c.catName}`} />
              <Field k="internal_category" v={c.area} />
              {c.result === "Fail" && <><Field k="failure_mode" v={c.mode} /><Field k="severity" v={c.severity} /></>}
              <Field k="turn_count" v={c.turnCount} />
              <Field k="infra_error" v={c.infra || "none"} />
              <Field k="source" v={c.source} />
              <Field k="adversary_goal" v={c.goal} />
              <Field k="rubric (fail if)" v={c.rubric} />
              <Field k="opening_question" v={c.question} />
            </>
          ) : (
            <>
              <Field k="source" v={c.source} />
              <Field k="attack" v={c.attack} />
              <Field k="category" v={c.category} />
              <Field k="turns_flagged" v={`${st!.flagged} of ${st!.scored}`} />
              <Field k="infra_errors" v={st!.abort} />
              {c.stop && Object.entries(c.stop).map(([k, v]) => <Field key={k} k={k} v={v} />)}
            </>
          )}
        </Box>

        <Box>
          {c.kind === "scenario" && (
            <Box sx={{ mx: 2, mt: 1, borderRadius: 2, p: 1.75, lineHeight: 1.6, bgcolor: c.result === "Fail" ? alpha(theme.palette.error.main, 0.08) : alpha(theme.palette.secondary.main, 0.1) }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 500, mb: 0.5 }}>Judge reasoning</Typography>
              <Typography variant="body2">{c.judge}</Typography>
            </Box>
          )}

          <Box sx={{ mx: 2, mt: 1.5, p: 1.5, border: 1, borderColor: "divider", borderRadius: 2, display: "grid", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {savedLocally
                ? "Rate the persona’s replies only: each reply below, and all of its replies together here. This file is open locally, so your reviews are saved in this browser only. Use Export report to share them."
                : "Rate the persona’s replies only: each reply below, and all of its replies together here. Reviews are independent: you see only your own ratings."}
            </Typography>
            {!savedLocally && (
              <Typography variant="body2" color="text.secondary">
                {reviewCount ? `${reviewCount} reviewer${reviewCount > 1 ? "s" : ""} so far${mine ? ", including you" : ""}.` : "No reviews yet."}
              </Typography>
            )}
            {controls("overall")}
            {mine && (
              <Box>
                <Button size="small" variant="outlined" color={armed ? "error" : "primary"} onClick={() => (armed ? onClear() : setArmed(true))}>
                  {armed ? "Click again to clear all your ratings and notes here" : "Clear my review"}
                </Button>
              </Box>
            )}
          </Box>
          {showTeam && reviews.some((r) => r.userId !== viewerId) && <TeamReviews reviews={reviews.filter((r) => r.userId !== viewerId)} />}

          <Stack spacing={1.5} sx={{ p: 2 }}>
            {c.kind === "scenario"
              ? c.turns.map((t, k) => (
                <Stack key={k} spacing={0.75}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>T{k + 1}</Typography>
                  <Message who="student (adversary)" text={t.user} />
                  <Message who={t.who.toLowerCase()} text={t.reply} reply />
                  {controls(k + 1)}
                </Stack>
              ))
              : c.rounds.map((r, k) => (
                <Stack key={k} spacing={0.75}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>T{k + 1}</Typography>
                    <OutcomePip outcome={r.outcome} />
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>{OUTCOME_LABEL[r.outcome]}</Typography>
                    {r.code && <Chip size="small" label={r.code} />}
                    {r.latencyMs != null && <Typography variant="caption" color="text.secondary">{(r.latencyMs / 1000).toFixed(2)} s</Typography>}
                  </Box>
                  {r.reason && <Typography variant="body2" color="text.secondary">{r.reason}</Typography>}
                  {r.sme && (
                    <Alert severity="info" icon={false} sx={{ py: 0 }}>
                      SME review: {[r.sme.agree && "agree: " + r.sme.agree, r.sme.correct && "correct verdict: " + r.sme.correct, r.sme.notes].filter(Boolean).join(" · ")}
                    </Alert>
                  )}
                  <Message who="attacker" text={r.sent} />
                  <Message who="persona reply" text={r.reply} reply />
                  {controls(k + 1)}
                </Stack>
              ))}
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
}
